import { useState, useCallback, useMemo } from 'react';
import axios from 'axios';
import { getIconComponent } from '../components/prd-workflow/foundation/iconUtils';

/**
 * Fallback build phases data for when backend data isn't available
 */
const FALLBACK_BUILD_PHASES = {
  'Setup & Infrastructure': [
    {
      id: 'setup-dev-environment',
      title: 'Setup Development Environment',
      icon: 'SettingsIcon',
      description: 'Configure development tools and environments',
      status: 'pending',
      order: 1,
      phase: 'Setup & Infrastructure'
    },
    {
      id: 'setup-version-control',
      title: 'Setup Version Control',
      icon: 'FolderIcon',
      description: 'Initialize repository and branching strategy',
      status: 'pending',
      order: 2,
      phase: 'Setup & Infrastructure'
    },
    {
      id: 'create-project-structure',
      title: 'Create Project Structure',
      icon: 'FolderIcon',
      description: 'Set up directories and initial project files',
      status: 'pending',
      order: 3,
      phase: 'Setup & Infrastructure'
    }
  ],
  'Core Components': [
    {
      id: 'setup-database',
      title: 'Database Setup',
      icon: 'ContentPasteIcon',
      description: 'Configure database and create schema',
      status: 'pending',
      order: 4,
      phase: 'Core Components'
    },
    {
      id: 'create-authentication',
      title: 'Authentication System',
      icon: 'SecurityIcon',
      description: 'Implement user authentication and authorization',
      status: 'pending',
      order: 5,
      phase: 'Core Components'
    },
    {
      id: 'create-api-layer',
      title: 'API Layer',
      icon: 'ApiIcon',
      description: 'Create API endpoints and controllers',
      status: 'pending',
      order: 6,
      phase: 'Core Components'
    }
  ],
  'Feature Implementation': [
    {
      id: 'implement-user-management',
      title: 'User Management',
      icon: 'PersonIcon',
      description: 'Implement user registration and profile management',
      status: 'pending',
      order: 7,
      phase: 'Feature Implementation'
    },
    {
      id: 'implement-core-feature-1',
      title: 'Core Feature 1',
      icon: 'FeaturedPlayListIcon',
      description: 'Implement main functionality',
      status: 'pending',
      order: 8,
      phase: 'Feature Implementation'
    },
    {
      id: 'implement-core-feature-2',
      title: 'Core Feature 2',
      icon: 'FeaturedPlayListIcon',
      description: 'Implement secondary functionality',
      status: 'pending',
      order: 9,
      phase: 'Feature Implementation'
    }
  ],
  'Testing & QA': [
    {
      id: 'unit-testing',
      title: 'Unit Testing',
      icon: 'ListAltIcon',
      description: 'Create and run unit tests',
      status: 'pending',
      order: 10,
      phase: 'Testing & QA'
    },
    {
      id: 'integration-testing',
      title: 'Integration Testing',
      icon: 'CodeIcon',
      description: 'Implement integration tests',
      status: 'pending',
      order: 11,
      phase: 'Testing & QA'
    },
    {
      id: 'ui-testing',
      title: 'UI Testing',
      icon: 'DesignServicesIcon',
      description: 'Test user interface components',
      status: 'pending',
      order: 12,
      phase: 'Testing & QA'
    }
  ],
  'Deployment & Release': [
    {
      id: 'setup-cicd',
      title: 'CI/CD Pipeline',
      icon: 'SettingsIcon',
      description: 'Configure continuous integration and deployment',
      status: 'pending',
      order: 13,
      phase: 'Deployment & Release'
    },
    {
      id: 'staging-deployment',
      title: 'Staging Deployment',
      icon: 'DevicesIcon',
      description: 'Deploy to staging environment',
      status: 'pending',
      order: 14,
      phase: 'Deployment & Release'
    },
    {
      id: 'production-deployment',
      title: 'Production Deployment',
      icon: 'DevicesIcon',
      description: 'Deploy to production environment',
      status: 'pending',
      order: 15,
      phase: 'Deployment & Release'
    }
  ]
};

/**
 * Custom hook to manage and organize build tasks by phase
 * 
 * @param {Object} params - Parameters for the hook
 * @param {Object} params.project - The project data
 * @param {Array} params.sections - Available sections
 * @param {boolean} params.isLoadingSections - Flag indicating if sections are loading
 * @param {Object} params.user - Current user information
 * @returns {Object} Build tasks hook results
 */
const useBuildTasks = ({ project, sections, isLoadingSections, user }) => {
  const [isGeneratingPlan, setIsGeneratingPlan] = useState(false);
  
  // Check if user has access to a section based on role
  const hasAccessToSection = (section) => {
    // Admin has access to all sections
    if (user?.systemRole === 'admin') return true;
    
    // Check if section has required roles and if user's role is included
    if (section.requiredRoles && section.requiredRoles.length > 0) {
      return section.requiredRoles.includes(user?.systemRole || 'user');
    }
    
    // If no required roles specified, default to accessible
    return true;
  };
  
  // Generate a build plan automatically using the backend API or locally
  const generateBuildPlan = useCallback(async (projectId) => {
    setIsGeneratingPlan(true);
    
    try {
      // Attempt to use backend API to generate plan if available
      const apiUrl = `/api/projects/${projectId}/build-plan`;
      await axios.post(apiUrl);
      
      // Reload project data to get updated tasks
      setTimeout(() => {
        setIsGeneratingPlan(false);
      }, 1000);
      
      return true;
    } catch (apiError) {
      console.error('API error generating build plan:', apiError);
      
      // If the API isn't available, simulate plan generation locally
      console.log('Falling back to local build plan generation');
      
      // Simulate API delay
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      setIsGeneratingPlan(false);
      
      // Here you would add tasks to the project
      // For now, this is a placeholder that doesn't actually modify the project
      return true;
    }
  }, []);
  
  // Process all project tasks to identify build-related tasks
  const buildTasks = useMemo(() => {
    // If sections are loading, return empty array
    if (isLoadingSections) {
      return [];
    }
    
    // Check if project has build tasks section
    if (!project || !project.buildTasks) {
      return [];
    }
    
    // Return build tasks with processed data
    return project.buildTasks;
  }, [project, isLoadingSections]);
  
  // Generate filtered and processed tasks
  const filteredTasks = useMemo(() => {
    // If backend data is loading, use the fallback data
    if (isLoadingSections || !sections || sections.length === 0 || isGeneratingPlan) {
      // Use fallback tasks with project status if available
      if (project && project.completedTasks && Array.isArray(project.completedTasks)) {
        // Create a flat array of all tasks from all phases
        const allTasks = Object.values(FALLBACK_BUILD_PHASES).flat();
        
        // Update task status based on completed tasks
        return allTasks.map(task => ({
          ...task,
          status: project.completedTasks.includes(task.id) ? 'completed' : 'pending',
          icon: getIconComponent(task.icon)
        }));
      }
      
      // Otherwise return all fallback tasks as pending
      return Object.values(FALLBACK_BUILD_PHASES)
        .flat()
        .map(task => ({
          ...task,
          icon: getIconComponent(task.icon)
        }));
    }
    
    // If build tasks exist, use them
    if (buildTasks && buildTasks.length > 0) {
      return buildTasks.map(task => ({
        ...task,
        icon: getIconComponent(task.icon || 'FolderIcon'),
        status: project && 
               project.completedTasks && 
               Array.isArray(project.completedTasks) && 
               project.completedTasks.includes(task.id) ? 'completed' : 'pending'
      }));
    }
    
    // If project data doesn't contain build tasks, use fallback
    return Object.values(FALLBACK_BUILD_PHASES)
      .flat()
      .map(task => ({
        ...task,
        icon: getIconComponent(task.icon)
      }));
  }, [buildTasks, project, isLoadingSections, sections, isGeneratingPlan]);
  
  // Group tasks by phase
  const groupedPhases = useMemo(() => {
    const phases = {};
    
    // If using fallback data
    if (filteredTasks.length === 0 || isGeneratingPlan) {
      return FALLBACK_BUILD_PHASES;
    }
    
    // Group by phase
    filteredTasks.forEach(task => {
      const phase = task.phase || 'Uncategorized';
      
      if (!phases[phase]) {
        phases[phase] = [];
      }
      
      phases[phase].push(task);
    });
    
    // Sort tasks within each phase by order
    Object.keys(phases).forEach(phase => {
      phases[phase].sort((a, b) => (a.order || 99) - (b.order || 99));
    });
    
    return phases;
  }, [filteredTasks, isGeneratingPlan]);
  
  return {
    filteredTasks,
    groupedPhases,
    isGeneratingPlan,
    generateBuildPlan
  };
};

export default useBuildTasks;